// Small presentational components shared across views.
import { useEffect, useRef, type ReactNode } from "react";
import type { Actor, Item, ItemSyncInfo, Priority } from "../api/types";
import { connectorLabel, formatItemDate, initials, isItemOverdue, PRIORITY_LABELS } from "../lib/format";

export function PriorityMarker({ priority }: { priority: Priority }) {
  if (!priority) return null;
  return (
    <span
      className={`prio prio-${priority}`}
      title={`Priority: ${PRIORITY_LABELS[priority]}`}
      aria-label={`Priority ${PRIORITY_LABELS[priority]}`}
      role="img"
    />
  );
}

export function DueChip({ item }: { item: Item }) {
  if (!item.due_at) return null;
  const overdue = isItemOverdue(item);
  return (
    <span className={`chip chip-date${overdue ? " overdue" : ""}`} title={overdue ? "Overdue" : "Due"}>
      {formatItemDate(item.due_at, item.due_has_time)}
    </span>
  );
}

export function LabelChips({ labels }: { labels: string[] }) {
  if (!labels.length) return null;
  return (
    <>
      {labels.map((l) => (
        <span key={l} className="chip chip-label">
          #{l}
        </span>
      ))}
    </>
  );
}

export function Avatar({ actor, size = 22 }: { actor: Actor | undefined; size?: number }) {
  const name = actor?.display_name ?? "Unknown";
  if (actor?.avatar_url) {
    return <img className="avatar" src={actor.avatar_url} alt={name} title={name} width={size} height={size} />;
  }
  return (
    <span
      className={`avatar${actor?.kind === "ghost" ? " ghost" : ""}`}
      style={{ width: size, height: size, fontSize: size * 0.42 }}
      title={name}
      aria-label={name}
      role="img"
    >
      {initials(name)}
    </span>
  );
}

export function AvatarStack({ ids, actors }: { ids: string[]; actors: Map<string, Actor> }) {
  if (!ids.length) return null;
  const shown = ids.slice(0, 3);
  return (
    <span className="avatar-stack">
      {shown.map((id) => (
        <Avatar key={id} actor={actors.get(id)} />
      ))}
      {ids.length > shown.length && <span className="avatar more">+{ids.length - shown.length}</span>}
    </span>
  );
}

const SYNC_STATE_TEXT: Record<ItemSyncInfo["state"], string> = {
  ok: "synced",
  pending_create: "pending",
  conflict: "conflict",
  error: "error",
};

export function SyncBadges({ sync }: { sync: ItemSyncInfo[] }) {
  if (!sync.length) return null;
  return (
    <>
      {sync.map((s) => {
        const label = connectorLabel(s.connector_type);
        const actedAs =
          s.acted_as === "user" ? " · pushed as you" : s.acted_as === "binding" ? " · via shared connection" : "";
        const title = s.last_error
          ? `${label}: ${SYNC_STATE_TEXT[s.state]}${actedAs} – ${s.last_error}`
          : `${label}: ${SYNC_STATE_TEXT[s.state]}${actedAs}`;
        const content = (
          <>
            <span className="dot" aria-hidden="true" />
            {label}
          </>
        );
        return s.remote_url ? (
          <a
            key={s.binding_id}
            className={`sync-badge ${s.state}`}
            href={s.remote_url}
            target="_blank"
            rel="noreferrer noopener"
            title={`${title} (open in ${label})`}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {content}
          </a>
        ) : (
          <span key={s.binding_id} className={`sync-badge ${s.state}`} title={title}>
            {content}
          </span>
        );
      })}
    </>
  );
}

export function ConnectorBadge({ type }: { type: string }) {
  return <span className="badge">{connectorLabel(type)}</span>;
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="spinner-wrap" role="status">
      <span className="spinner" aria-hidden="true" />
      <span className="muted">{label}</span>
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {children}
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div className="error-box" role="alert">
      <span>{error}</span>
      {onRetry && (
        <button type="button" className="btn small" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

/** Accessible modal dialog using the native <dialog> element. */
export function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className={`modal${wide ? " wide" : ""}`}
      aria-label={title}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="modal-head">
        <h2>{title}</h2>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      <div className="modal-body">{children}</div>
    </dialog>
  );
}
